import { useState, type ChangeEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/api/endpoints';
import type { Match, Player, Team } from '@/api/types';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { formatDateTime } from '@/lib/datetime';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

const TARGET_TEAM_HINT = 'münster';

function sanitizeGoals(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 2);
}

function sanitizeAttendance(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}

/**
 * API -> Formular-Entwurf: `null` (noch nicht bestätigt) wird zu einer leeren
 * Auswahl (nichts angeklickt); `[]` (bestätigt: keiner) wird zum aktiven
 * "Keiner"-Chip (`[null]`); `[ids]` bleibt unverändert.
 */
function toDraft(ids: number[] | null): (number | null)[] {
  if (ids === null) return [];
  return ids.length > 0 ? ids : [null];
}

interface LoadedData {
  matches: Match[];
  players: Player[];
}

export function AdminMatchResultsPage() {
  const { id } = useParams<{ id: string }>();
  const matchdayId = Number(id);

  const { data, loading, error, reload } = useAsync<LoadedData>(
    async (signal) => {
      const [matches, teams] = await Promise.all([
        adminApi.matchdayMatches(matchdayId, signal),
        adminApi.listTeams(signal).catch(() => [] as Team[]),
      ]);
      const target =
        teams.find((t) => t.name.toLowerCase().includes(TARGET_TEAM_HINT)) ?? teams[0] ?? null;
      const players = target ? await adminApi.listPlayers(target.id, signal).catch(() => []) : [];
      return { matches, players };
    },
    [matchdayId],
  );

  if (Number.isNaN(matchdayId)) return <Alert>Ungültiger Spieltag.</Alert>;
  if (loading) return <Spinner label="Spiele werden geladen…" />;
  if (error) return <Alert>{error}</Alert>;

  const matches = data?.matches ?? [];
  const players = data?.players ?? [];

  return (
    <>
      <p className="muted">
        <Link to="/admin/matchdays">← Spieltage</Link>
      </p>
      <h2>Ergebnisse eingeben</h2>
      {matches.length === 0 && <Card>Keine Spiele hinterlegt.</Card>}
      {matches.map((m) => (
        <ResultCard key={m.id} match={m} players={players} onSaved={reload} />
      ))}
    </>
  );
}

function ResultCard({
  match,
  players,
  onSaved,
}: {
  match: Match;
  players: Player[];
  onSaved: () => void;
}) {
  const [home, setHome] = useState(match.home_goals !== null ? String(match.home_goals) : '');
  const [away, setAway] = useState(match.away_goals !== null ? String(match.away_goals) : '');
  const [attendance, setAttendance] = useState(
    match.attendance != null ? String(match.attendance) : '',
  );
  // null-Eintrag = "keiner" explizit gewählt (verhält sich wie ein normales
  // Kadermitglied); leere Liste = Sektion noch nicht angefasst -> beim
  // Speichern weglassen, damit unberührte Sektionen nicht versehentlich als
  // "bestätigt: keiner" gewertet werden.
  const [scorers, setScorers] = useState<(number | null)[]>(toDraft(match.scorer_player_ids));
  const [assists, setAssists] = useState<(number | null)[]>(toDraft(match.assist_player_ids));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedInfo, setSavedInfo] = useState<string | null>(null);

  const handle = (set: (v: string) => void) => (e: ChangeEvent<HTMLInputElement>) =>
    set(sanitizeGoals(e.target.value));

  // "keiner" schließt echte Spieler aus und umgekehrt (genau ein Zustand gilt).
  function toggle(
    list: (number | null)[],
    setList: (v: (number | null)[]) => void,
    pid: number | null,
  ) {
    if (list.includes(pid)) {
      setList(list.filter((x) => x !== pid));
    } else if (pid === null) {
      setList([null]);
    } else {
      setList([...list.filter((x) => x !== null), pid]);
    }
  }

  // 'live' = Live-Korrektur, Spiel läuft weiter (LIVE-Anzeige bleibt);
  // 'finished' = Spiel beenden, Wertung ist final.
  async function save(nextStatus: 'live' | 'finished') {
    setBusy(true);
    setError(null);
    setSavedInfo(null);
    try {
      const res = await adminApi.putMatchResult(match.id, {
        home_goals: Number(home),
        away_goals: Number(away),
        status: nextStatus,
        attendance: attendance === '' ? null : Number(attendance),
        scorer_player_ids: scorers.length > 0 ? scorers : undefined,
        assist_player_ids: assists.length > 0 ? assists : undefined,
      });
      const s = res.scored;
      const total = s.result + s.attendance + s.scorer + s.assist;
      setSavedInfo(
        `Gespeichert — ${total} Tipp(s) bewertet ` +
          `(Ergebnis ${s.result}, Zuschauer ${s.attendance}, Tor ${s.scorer}, Assist ${s.assist}).`,
      );
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  const canSave = home !== '' && away !== '' && !busy;

  return (
    <Card>
      <div className={styles.rowTitle}>
        {match.home_team.name} – {match.away_team.name}
      </div>
      <div className="muted" style={{ fontSize: '0.8rem', marginBottom: 8 }}>
        {formatDateTime(match.kickoff_at)}
        {match.status === 'finished' ? ' · ausgewertet' : ''}
      </div>

      <div className={styles.resultInputs} style={{ marginBottom: 12 }}>
        <input
          className={styles.resultInput}
          type="text"
          inputMode="numeric"
          aria-label={`Tore ${match.home_team.name}`}
          value={home}
          onChange={handle(setHome)}
        />
        <span>:</span>
        <input
          className={styles.resultInput}
          type="text"
          inputMode="numeric"
          aria-label={`Tore ${match.away_team.name}`}
          value={away}
          onChange={handle(setAway)}
        />
      </div>

      <label className={styles.field} style={{ flex: '1 1 160px' }}>
        <span className={styles.label}>Zuschauerzahl</span>
        <input
          className={styles.input}
          type="text"
          inputMode="numeric"
          placeholder="z. B. 9300"
          value={attendance}
          onChange={(e) => setAttendance(sanitizeAttendance(e.target.value))}
        />
      </label>

      {players.length > 0 && (
        <>
          <PlayerMultiSelect
            label="Torschützen"
            players={players}
            selected={scorers}
            onToggle={(pid) => toggle(scorers, setScorers, pid)}
          />
          <PlayerMultiSelect
            label="Assists"
            players={players}
            selected={assists}
            onToggle={(pid) => toggle(assists, setAssists, pid)}
          />
        </>
      )}

      <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {match.status === 'finished' ? (
          <Button className={styles.btnSm} disabled={!canSave} onClick={() => void save('finished')}>
            {busy ? '…' : 'Ergebnis speichern'}
          </Button>
        ) : (
          <>
            {/* Läuft weiter: Live-Stand übernehmen, LIVE-Anzeige bleibt. */}
            <Button className={styles.btnSm} disabled={!canSave} onClick={() => void save('live')}>
              {busy ? '…' : 'Speichern (läuft weiter)'}
            </Button>
            {/* Final: Wertung steht fest, LIVE endet. */}
            <Button
              className={styles.btnSm}
              variant="ghost"
              disabled={!canSave}
              onClick={() => void save('finished')}
            >
              Speichern &amp; beenden
            </Button>
          </>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {savedInfo && <div className={styles.savedHint}>{savedInfo}</div>}
    </Card>
  );
}

function PlayerMultiSelect({
  label,
  players,
  selected,
  onToggle,
}: {
  label: string;
  players: Player[];
  selected: (number | null)[];
  onToggle: (pid: number | null) => void;
}) {
  return (
    <fieldset className={styles.multiSelect}>
      <legend className={styles.label}>{label}</legend>
      <div className={styles.chips}>
        {/* "Keiner" verhält sich wie ein normales Kadermitglied: ein Chip,
            der bewusst angeklickt werden muss -- keine Vorauswahl. */}
        <button
          type="button"
          className={selected.includes(null) ? `${styles.chip} ${styles.chipOn}` : styles.chip}
          onClick={() => onToggle(null)}
        >
          keiner
        </button>
        {players.map((p) => {
          const on = selected.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
              onClick={() => onToggle(p.id)}
            >
              {p.name}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
